import {Box, useTheme} from "@mui/material";
import {alpha, lighten} from "@mui/material/styles";
import * as React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from "recharts";
import {NEGATIVE_COLOR, POSITIVE_COLOR, TARGET_COLOR} from "../../../insights/kit/insightsColors";
import {Verdict, verdictColor} from "../../../insights/kit/insightsFormat";
import {InsightsLegend, LegendEntry} from "../../../insights/kit/InsightsLegend";
import {InsightsTooltipPanel, TooltipRow, TooltipTitle} from "../../../insights/kit/InsightsTooltip";
import {BAR_DRAW_DURATION, MARKER_FADE_DELAY, MARKER_FADE_DURATION} from "../constants";

export interface SurveyStepPoint {
    step: number;
    label: string;
    question: string;
    reached: number;
    dropped: number;
    /** Fill-outs this question was put to at all; null on an API that does not count them yet. */
    eligible: number | null;
}

export interface SurveyStepRow extends SurveyStepPoint {
    dropRate: number;
    survival: number;
    compareReached: number | null;
    compareEligible: number | null;
    compareSurvival: number | null;
}

export interface SurveyHealthLabels {
    reached: string;
    dropped: string;
    dropRate: string;
    survival: string;
    compareReached: string;
    target: string;
    aboveTarget: string;
    belowTarget: string;
}

export function stepVerdict(row: SurveyStepRow, target: number | undefined): Verdict {
    if (target == null) return "neutral";

    return row.survival >= target ? "positive" : "negative";
}

export interface SurveyHealthSpec {
    rows: SurveyStepRow[];
    target?: number;
    labels: SurveyHealthLabels;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
}

const TARGET_WIDTH = 1.5;
const BAR_RADIUS = 4;

const hasCompareRows = (rows: SurveyStepRow[]) => rows.some(row => row.compareReached != null);

function useBarColors(target: number | undefined) {
    const theme = useTheme();
    const neutral = theme.palette.primary.main;

    return {
        compare: lighten(theme.palette.primary.main, 0.45),
        barColor: (row: SurveyStepRow) => verdictColor(stepVerdict(row, target), neutral),
        neutral
    };
}

interface TotalChipProps {
    x?: number | string;
    y?: number | string;
    width?: number | string;
    value?: number | string;
    format: (value: number) => string;
    background: string;
    borderColor: string;
    textColor: string;
    fontSize?: number;
    fontWeight?: number;
}

const CHIP_HEIGHT = 22;
const CHIP_MIN_BAR_WIDTH = 50;
const CHIP_MAX_OVERHANG = 8;

function TotalChip({
    x,
    y,
    width,
    value,
    format,
    background,
    borderColor,
    textColor,
    fontSize = 12,
    fontWeight = 600
}: TotalChipProps) {
    const total = Number(value);

    if (x == null || y == null || width == null || Number.isNaN(total)) {
        return null;
    }

    const barWidth = Number(width);
    const label = format(total);
    const chipWidth = label.length * (fontSize * 0.625) + 16;

    if (barWidth < CHIP_MIN_BAR_WIDTH || chipWidth > barWidth + CHIP_MAX_OVERHANG) {
        return null;
    }

    const centerX = Number(x) + barWidth / 2;
    const centerY = Number(y) - CHIP_HEIGHT / 2 - 6;

    return (
        <g pointerEvents={"none"}>
            <rect
                x={centerX - chipWidth / 2}
                y={centerY - CHIP_HEIGHT / 2}
                width={chipWidth}
                height={CHIP_HEIGHT}
                rx={6}
                fill={background}
                stroke={borderColor}
            />

            <text
                x={centerX}
                y={centerY}
                textAnchor={"middle"}
                dominantBaseline={"middle"}
                fontSize={fontSize}
                fontWeight={fontWeight}
                fill={textColor}
            >
                {label}
            </text>
        </g>
    );
}

interface DropMarkerProps {
    x?: number | string;
    y?: number | string;
    width?: number | string;
    value?: number | string;
    marked: ReadonlySet<number>;
}

/** A dot over each question the list under the chart names - only where the bar is too narrow for its count chip. */
function DropMarker({x, y, width, value, marked}: DropMarkerProps) {
    if (x == null || y == null || width == null || !marked.has(Number(value))) return null;
    if (Number(width) >= CHIP_MIN_BAR_WIDTH) return null;

    return <circle cx={Number(x) + Number(width) / 2} cy={Number(y) - 7} r={3} fill={NEGATIVE_COLOR} pointerEvents={"none"}/>;
}

interface StepTooltipProps {
    active?: boolean;
    payload?: Array<{payload?: SurveyStepRow}>;
    barColor: (row: SurveyStepRow) => string;
    compareColor: string;
    labels: SurveyHealthLabels;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
}

function StepTooltip({active, payload, barColor, compareColor, labels, formatCount, formatPercent}: StepTooltipProps) {
    const row = payload?.[0]?.payload;

    if (!active || row == null) {
        return null;
    }

    // "3 / 4 · 75 %" - the share means nothing without the number it was taken out of.
    const outOf = (reached: number, eligible: number | null, share: number) => (
        eligible == null
            ? `${formatCount(reached)} · ${formatPercent(share)}`
            : `${formatCount(reached)} / ${formatCount(eligible)} · ${formatPercent(share)}`
    );

    return (
        <InsightsTooltipPanel>
            <TooltipTitle title={row.label} subtitle={row.question}/>

            <TooltipRow
                swatch={barColor(row)}
                label={labels.reached}
                value={outOf(row.reached, row.eligible, row.survival)}
            />

            {row.compareReached != null && (
                <TooltipRow
                    swatch={compareColor}
                    label={labels.compareReached}
                    value={outOf(row.compareReached, row.compareEligible, row.compareSurvival ?? 0)}
                />
            )}

            <TooltipRow label={labels.dropped} value={formatCount(row.dropped)}/>
            <TooltipRow label={labels.dropRate} value={formatPercent(row.dropRate)}/>
        </InsightsTooltipPanel>
    );
}

export function SurveyHealthLegend({rows, target, labels, formatPercent}: SurveyHealthSpec) {
    const {compare, neutral} = useBarColors(target);

    return (
        <InsightsLegend>
            {target == null ? (
                <LegendEntry shape={"bar"} color={neutral} label={labels.reached}/>
            ) : (
                <React.Fragment>
                    <LegendEntry shape={"bar"} color={POSITIVE_COLOR} label={labels.aboveTarget}/>
                    <LegendEntry shape={"bar"} color={NEGATIVE_COLOR} label={labels.belowTarget}/>
                    <LegendEntry color={TARGET_COLOR} width={TARGET_WIDTH} label={`${labels.target} ${formatPercent(target)}`}/>
                </React.Fragment>
            )}

            {hasCompareRows(rows) && <LegendEntry shape={"bar"} color={compare} label={labels.compareReached}/>}
        </InsightsLegend>
    );
}

interface ChartProps extends SurveyHealthSpec {
    height: number;
    /** Steps the card names as its biggest drop-offs. */
    marked?: ReadonlySet<number>;
}

const NOTHING_MARKED: ReadonlySet<number> = new Set();

export function SurveyHealthChart({rows, target, labels, formatCount, formatPercent, height, marked = NOTHING_MARKED}: ChartProps) {
    const theme = useTheme();
    const {compare, barColor, neutral} = useBarColors(target);

    const hasCompare = hasCompareRows(rows);

    const [animate, setAnimate] = React.useState(true);

    React.useEffect(() => {
        setAnimate(true);

        const timer = window.setTimeout(() => setAnimate(false), BAR_DRAW_DURATION + 400);

        return () => window.clearTimeout(timer);
    }, [hasCompare]);

    return (
        <Box
            sx={{
                width: "100%",
                minWidth: 0,
                height,

                "@keyframes healthTargetFadeIn": {from: {opacity: 0}, to: {opacity: 1}},
                "& .recharts-reference-line": {
                    animation: `healthTargetFadeIn ${MARKER_FADE_DURATION}ms ease-out ${MARKER_FADE_DELAY}ms both`
                }
            }}
        >
            <ResponsiveContainer width={"100%"} height={"100%"} minWidth={0}>
                <BarChart
                    data={rows}
                    margin={{top: 26, right: 8, bottom: 0, left: 0}}
                    accessibilityLayer={false}
                >
                    <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeOpacity={0.8}/>

                    <XAxis
                        dataKey={"label"}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={12}
                        height={34}
                        tick={{fill: theme.palette.text.secondary, fontSize: 12}}
                    />

                    <YAxis
                        width={48}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={8}
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        tick={{fill: theme.palette.text.secondary, fontSize: 11}}
                        tickFormatter={value => `${value} %`}
                    />

                    {target != null && (
                        <ReferenceLine y={target} stroke={TARGET_COLOR} strokeWidth={TARGET_WIDTH} strokeOpacity={0.8}/>
                    )}

                    <RechartsTooltip
                        isAnimationActive={false}
                        offset={14}
                        cursor={{fill: alpha(theme.palette.text.primary, 0.05)}}
                        wrapperStyle={{pointerEvents: "none", transition: "none", outline: "none", zIndex: 10}}
                        content={
                            <StepTooltip
                                barColor={barColor}
                                compareColor={compare}
                                labels={labels}
                                formatCount={formatCount}
                                formatPercent={formatPercent}
                            />
                        }
                    />

                    <Bar
                        dataKey={"survival"}
                        fill={neutral}
                        radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]}
                        maxBarSize={56}
                        isAnimationActive={animate}
                        animationDuration={BAR_DRAW_DURATION}
                        animationEasing={"ease-out"}
                    >
                        {rows.map(row => (
                            <Cell key={row.step} fill={barColor(row)}/>
                        ))}

                        <LabelList
                            dataKey={"reached"}
                            content={
                                <TotalChip
                                    format={formatCount}
                                    background={theme.palette.background.paper}
                                    borderColor={theme.palette.divider}
                                    textColor={theme.palette.text.primary}
                                />
                            }
                        />

                        <LabelList dataKey={"step"} content={<DropMarker marked={marked}/>}/>
                    </Bar>

                    {hasCompare && (
                        <Bar
                            dataKey={"compareSurvival"}
                            fill={compare}
                            radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]}
                            maxBarSize={56}
                            isAnimationActive={animate}
                            animationDuration={BAR_DRAW_DURATION}
                            animationEasing={"ease-out"}
                        >
                            <LabelList
                                dataKey={"compareReached"}
                                content={
                                    <TotalChip
                                        format={formatCount}
                                        background={theme.palette.background.paper}
                                        borderColor={alpha(compare, 0.5)}
                                        textColor={theme.palette.text.secondary}
                                        fontSize={11}
                                        fontWeight={500}
                                    />
                                }
                            />
                        </Bar>
                    )}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
